<template>
  <div>
    <vee-form v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <div v-if="Object.keys(errors).length > 0" class="row">
        <div class="col-12 alert alert-danger">
          <h3>
            There was an error processing your request
          </h3>
          <div v-for="(vals, scope) in errors" :key="scope">
            <h4>{{capitalize(scope)}}</h4>
            <dl class="row">
              <div v-for="(valErrors, field) in vals" :key="field">
                <dt class="col-sm-2">{{capitalize(userName(field))}}</dt>
                <dd :class="'col-sm-10 ' + field + '-error-msg'">{{valErrors.join(", ")}}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <h1>Bill Payment</h1>
          <p>
          Use this page to submit a payment for an invoice you've received, such as for advertising with the paper. <strong>To pay for a subscription to the paper, go <a href="/subscribers/sign_up">here</a>.</strong>
          </p>
          <p>
          If you have any questions about how to pay your bill, <strong>give us a call @ {{billingPhone}}</strong>
          </p>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <h2>Your info</h2>
        </div>
      </div>

      <div class="row form-group">
        <div class="col-xs-8 col-md-3">
          <text-input
            name="name"
            label="Your name"
            v-model="user.name"
          />
        </div>

        <div class="col-xs-8 col-md-5">
          <text-input
            name="company"
            label="Company name (as it appears on your statement)"
            v-model="user.company"
          />
        </div>
      </div>

      <div class="row form-group">
        <div class="col-xs-8 col-md-3">
          <text-input
            name="phone"
            label="Your phone number"
            v-model="user.phone"
          />
        </div>

        <div class="col-xs-8 col-md-5">
          <text-input
            name="email"
            label="Your email"
            v-model="user.email"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-8">
          <h2>Payment info</h2>
        </div>
      </div>

      <div class="row form-group">
        <div>
          <div class="col-xs-8 col-md-3">
            <text-currency-input
              name="amount"
              :placeholder="false"
              label="Payment amount"
              v-model:cents="payment.amountCents"
            />
          </div>
          <div class="hidden-xs hidden-sm col-md-12">&nbsp;</div>
        </div>

        <div class="col-xs-8 col-md-3">
          <text-input
            name="account"
            label="Account Number"
            v-model="payment.account"
          />
        </div>

        <div class="col-xs-8 col-md-5">
          <text-input
            name="invoice"
            label="Invoice(s)"
            v-model="payment.invoice"
          />
        </div>
      </div>
      <div class="row form-group">
        <div class="col-xs-8">
          <text-input
            name="comments"
            label="Comments to staff"
            v-model="comment"
          />
        </div>
      </div>

      <div class="row form-group">
        <div class="col-xs-8">
          <credit-card-input @submit="handleSubmit($event, submit)" :submitEnabled="!awaitingResponse" />
        </div>
      </div>

      <loading-spinner :display="awaitingResponse && spinnerEnabled" />
    </vee-form>
  </div>
</template>

<script>
import { ref, inject } from "vue"
import BillPayment from "@/lib/BillPayment"
import CreditCardInput from "@/components/layout/forms/CreditCardInput"
import { Form as VeeForm } from "vee-validate"
import * as Yup from 'yup'
import { generateIdempotencyNonce } from "@/lib/idempotencyNonce"

export default {
  components: {CreditCardInput, VeeForm},
  setup(props, {emit}) {
    const errors = ref({})

    const schema = Yup.object().shape({
      name: Yup.string().required().label('Name'),
      phone: Yup.string().required().label('Phone number'),
      email: Yup.string().required().email().label('Email'),
      amount: Yup.string().required().matches(/^\d*\.?\d{0,2}$/, {message: "Amount is invalid"}).label('Amount'),
    })

    let idempotencyNonce = generateIdempotencyNonce()

    const viewState = {
      user: ref({
        name: "",
        company: "",
        email: "",
        phone: ""
      }),
      payment: ref({
        invoice: "",
        account: "",
        amountCents: undefined
      }),
      comment: ref(""),
      billingPhone: ref("(000) 000-0000"),
      awaitingResponse: ref(false),
      spinnerEnabled: ref(true)
    }

    const workbench = inject('workbench')
    workbench.configItem('billing_phone')
      .then((billingPhone) => viewState.billingPhone.value = billingPhone)

    function sendToBackend(paymentToken) {
      viewState.awaitingResponse.value = true
      const paymentInfo = {
        ...viewState.payment.value,
        paymentToken: paymentToken
      }

      BillPayment.create(
        workbench,
        viewState.user.value,
        paymentInfo,
        viewState.comment.value,
        idempotencyNonce
      )
      .then((resp) => {
        if (resp.ok) {
          resp.text().then((slug) => emit('success', slug))
          viewState.awaitingResponse.value = false
        } else {
          resp.json()
          .then((json) => {
            errors.value = json
            // Reached the backend and failed (e.g. card decline): the key is spent,
            // so mint a fresh nonce for the retry. Network errors fall to .catch below
            // and keep the nonce so Stripe can dedupe an unknown-outcome resubmit.
            idempotencyNonce = generateIdempotencyNonce()
            viewState.awaitingResponse.value = false
            window.scrollTo(0,0)
          })
          .catch((err) => {
            errors.value = {payment: {payment_token: [`An error was encountered while taking your payment. Please do not resubmit and call us at ${viewState.billingPhone.value} to verify if the payment was completed.`]}}
            viewState.spinnerEnabled.value = false
            window.scrollTo(0,0)
          })
        }
      })
      .catch((err) => console.error("Error submitting form:", err))
    }

    function submit(arg, { evt }) {
      if (evt) {
        sendToBackend(evt)
      }
    }

    function capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function userName(field) {
      switch (field) {
        case "amount_cents": return "amount"
        case "credit_card": return "credit card"
        case "payment_token": return "processing"
        default: return field
      }
    }

    return {
      ...viewState,
      errors,
      capitalize,
      userName,
      schema,
      submit,
      console,
      workbench // for tests
    }
  },
}
</script>
